import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { connect, useDispatch } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import {
  BackAndMultipleButtons,
  SaveButton,
  SaveTempButton,
} from 'components/base/BottomStepComponent';
import ListButtonsGroup from 'components/base/ListButtonsGroup';
import {
  APIStatus,
  ActionType,
  ApprovalStatus,
} from 'config/CustomEnums';
import { PermissionCodes } from 'config/PermissionCodes';
import { validate } from 'containers/engagement/campaign/CreateCampaignValidate';

import { scrollToFirstError } from './CreateCampaignHandleError';
import PublishCampaignPrompt from './PublishCampaignPrompt';
import RejectPrompt from './RejectPrompt';

import { createAction } from 'utils';

import './CreateCampaignStepFour.scss';
import BaseCreationStepContentByConfig from 'components/base/BaseCreationStepContentByConfig';
import { CREATE_STEP_FOUR_CONFIG } from 'containers/engagement/campaign/CampaignCreationConfig';
import RecurringMessageSendToAllAgainPrompt from './RecurringMessageSendToAllAgainPrompt';

function CreateCampaignStepFourV2({
  createStatus,
  formHasSubmitted,
  listpath,
  isSuperuser,
  userPermissions,
  campaignId,
}) {
  const params = useParams();
  const { getValues, setValue, clearErrors, setError, formState } = useFormContext();
  const errors = formState.errors;
  const location = useLocation();

  const [showPublishPrompt, setShowPublishPrompt] = useState(false);
  const [showRejectPrompt, setShowRejectPrompt] = useState(false);
  const [showSendToAllPrompt, setShowSendToAllPrompt] = useState(false);
  const [actionType, setActionType] = useState(ActionType.none);
  const approvalStatus = getValues('approvalStatus');
  const campaignType = getValues('campaignType');
  const isBirthdayRecurringCampaign = getValues('isBirthdayRecurringCampaign');
  const recurringMessageChannels = getValues('recurringMessage.channels');

  const dispatch = useDispatch();
  const history = useHistory();
  const hasPermission = (requires) => {
    if (isSuperuser) return true;
    if (userPermissions.includes(requires) || !requires) return true;

    return false;
  };

  useEffect(() => {
    scrollToFirstError(errors, ['checkList']);
  }, [errors]);

  useEffect(() => {
    if (createStatus === APIStatus.success && formHasSubmitted) {
      setShowPublishPrompt(
        actionType !== ActionType.approval ||
        history.location.pathname.includes('edit'),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createStatus, formHasSubmitted]);

  const stepChangeAction = (checkCheckList, isBack, buttonAction = null) => {
    const isValid = validate({
      getValues: getValues,
      setError,
      clearErrors,
      step: 3,
      isBack,
      checkCheckList,
    });
    if (!isValid || isBack) {
      dispatch({
        type: 'createCampaign/stepChange',
        payload: { isBack: isBack, step: 3, isValid },
      });
    } else {
      setActionType(buttonAction);
      if (
        buttonAction === ActionType.withdraw ||
        buttonAction === ActionType.unpublish
      ) {
        dispatch(
          createAction('createCampaign/updateApprovalStatus')({
            data: getValues(),
            actionType: buttonAction,
          }),
        );
      } else {
        // show recurring message send to all prompt
        if (
          isBirthdayRecurringCampaign &&
          [ActionType.forcePublish, ActionType.approval].includes(buttonAction) &&
          recurringMessageChannels &&
          recurringMessageChannels?.length > 0
        ) {
          setShowSendToAllPrompt(true);
          return;
        }
        dispatch(
          createAction('createCampaign/createOrUpdateCampaign')({
            data: getValues(),
            actionType: buttonAction,
            resume: location.state?.resume,
          }),
        );
      }
    }
  };

  const approvalButtonAction = () => {
    if (approvalStatus === ApprovalStatus.published) {
      stepChangeAction(false, false, saveActionType);
    } else {
      stepChangeAction(true, false, saveActionType);
    }
  };

  const forcePublishButtonAction = () => {
    stepChangeAction(true, false, ActionType.forcePublish);
  };

  const getTempText = () => {
    switch (approvalStatus) {
      case ApprovalStatus.draft:
        return 'Update';
      case ApprovalStatus.pending:
        if (hasPermission(PermissionCodes.publishPendingCampaign)) {
          return 'Update';
        }
        return 'Withdraw';
      case ApprovalStatus.published:
        return 'Update';
      default:
        return 'Save as a draft';
    }
  };

  const tempActionType = useMemo(() => {
    switch (approvalStatus) {
      case ApprovalStatus.draft:
        return ActionType.update;
      case ApprovalStatus.pending:
        if (hasPermission(PermissionCodes.publishPendingCampaign)) {
          return ActionType.update;
        }
        return ActionType.withdraw;
      case ApprovalStatus.published:
        return ActionType.tempForPulished;
      default:
        return ActionType.save;
    }
  }, [approvalStatus]);

  const tempRequires = () => {
    switch (approvalStatus) {
      case ApprovalStatus.draft:
        return PermissionCodes.editDraftCampaign;
      case ApprovalStatus.pending:
        if (hasPermission(PermissionCodes.publishPendingCampaign)) {
          return PermissionCodes.publishPendingCampaign;
        }
        return PermissionCodes.withdrawPendingCampaignToDraft;
      case ApprovalStatus.published:
        return PermissionCodes.tempForPublishCampaign;
      default:
        return PermissionCodes.createDraftCampaign;
    }
  };

  const approvalText = () => {
    switch (approvalStatus) {
      case ApprovalStatus.published:
        return 'Unpublish';
      case ApprovalStatus.pending:
        if (hasPermission(PermissionCodes.publishPendingCampaign)) {
          return 'Approve';
        }
        return 'Pending for approval';
      case ApprovalStatus.draft:
      default:
        return 'Submit for approval';
    }
  };

  const saveActionType = useMemo(() => {
    switch (approvalStatus) {
      case ApprovalStatus.published:
        return ActionType.unpublish;
      case ApprovalStatus.pending:
        if (hasPermission(PermissionCodes.publishPendingCampaign)) {
          return ActionType.approval;
        }
        return ActionType.pendingForApproval;
      case ApprovalStatus.draft:
      default:
        return ActionType.submitForApproval;
    }
  }, [approvalStatus]);

  const saveRequires = () => {
    switch (approvalStatus) {
      case ApprovalStatus.pending:
        if (hasPermission(PermissionCodes.publishPendingCampaign)) {
          return PermissionCodes.publishPendingCampaign;
        }
        return PermissionCodes.withdrawPendingCampaignToDraft;
      case ApprovalStatus.published:
        if (hasPermission(PermissionCodes.withdrawPublishCampaignToPending)) {
          return PermissionCodes.withdrawPublishCampaignToPending;
        }
        return PermissionCodes.withdrawPublishCampaignToDraft;
      case ApprovalStatus.draft:
      default:
        return PermissionCodes.pendingCampaign;
    }
  };

  const showForceSavePermission = () => {
    if (
      approvalStatus == ApprovalStatus.pending ||
      approvalStatus == ApprovalStatus.published
    ) {
      return false;
    }
    return hasPermission(PermissionCodes.publishPendingCampaign);
  };

  const extraPopContent = useMemo(() => {
    const extraPopContentList = [
      {
        content: getTempText(),
        action: () => stepChangeAction(false, false, tempActionType),
      },
    ];

    if (approvalStatus === ApprovalStatus.pending) {
      extraPopContentList.push({
        content: 'Reject',
        action: () => {
          setShowRejectPrompt(true);
        },
        requires: PermissionCodes.publishPendingCampaign,
      });
    }
    return extraPopContentList;
  }, [approvalStatus]);

  const extraButtons = useMemo(() => {
    const buttonList = [];
    if (hasPermission(tempRequires())) {
      buttonList.push(
        <SaveTempButton
          text={getTempText()}
          action={() => stepChangeAction(false, false, tempActionType)}
        />,
      );
    }
    if (
      approvalStatus === ApprovalStatus.pending &&
      hasPermission(PermissionCodes.publishPendingCampaign)
    ) {
      buttonList.push(
        <SaveTempButton
          text="Reject"
          action={() => {
            setShowRejectPrompt(true);
          }}
        />,
      );
    }
    if (showForceSavePermission() && hasPermission(saveRequires())) {
      buttonList.push(
        <SaveTempButton
          text={approvalText()}
          action={() => approvalButtonAction()}
          disabled={
            saveActionType === ActionType.pendingForApproval ? true : false
          }
        />,
      );
    }
    return buttonList;
  }, [approvalStatus]);

  const primaryButton = useMemo(() => {
    if (showForceSavePermission()) {
      return (
        <SaveButton
          text={'Skip approval and Publish'}
          action={() => forcePublishButtonAction()}
        />
      );
    }
    if (hasPermission(saveRequires())) {
      return (
        <SaveButton
          text={approvalText()}
          action={() => approvalButtonAction()}
          disabled={
            saveActionType === ActionType.pendingForApproval ? true : false
          }
        />
      );
    }
    return null;
  }, [approvalStatus]);

  return (
    <>
      <BaseCreationStepContentByConfig
        configuration={CREATE_STEP_FOUR_CONFIG}
        hidePreview={true}
      />

      <BackAndMultipleButtons
        backAction={() => stepChangeAction(false, true)}
        multipleButtons={[
          <ListButtonsGroup
            hideExtraButtonWidth={600}
            hideAllButtonWidth={400}
            hideExtraPopPosition="top"
            hideAllPopPosition="topRight"
            extraButtons={extraButtons}
            extraPopContent={extraPopContent}
            primaryButton={primaryButton}
            primaryPopContent={{
              requires: saveRequires(),
              action: () => approvalButtonAction(),
              content: approvalText(),
            }}
          />,
        ]}
      />

      <RejectPrompt
        show={showRejectPrompt}
        onHide={() => setShowRejectPrompt(false)}
        onConfirm={(comment) => {
          setShowRejectPrompt(false);
          dispatch(
            createAction('createCampaign/rejectCampaign')({
              id: params.id,
              message: comment,
              afterAction: () => {
                const pathname = location.pathname.split('/')[1];
                history.push({
                  pathname: `/${pathname}`,
                });
              },
            }),
          );
        }}
      />

      <RecurringMessageSendToAllAgainPrompt
        show={showSendToAllPrompt}
        onHide={() => setShowSendToAllPrompt(false)}
        onConfirm={(sendToAllAgain) => {
          setShowSendToAllPrompt(false);
          setValue(`recurringMessage.sendToAllAgain`, sendToAllAgain, { shouldDirty: true })
          dispatch(
            createAction('createCampaign/createOrUpdateCampaign')({
              data: getValues(),
              actionType: actionType,
              resume: location.state?.resume,
            }),
          );
        }}
      />

      <PublishCampaignPrompt
        campaignId={campaignId}
        campaignType={campaignType}
        actionType={actionType}
        show={showPublishPrompt}
        path={listpath}
      />
    </>
  );
}

const mapPropsToState = (state) => ({
  createStatus: state.createCampaign.createStatus,
  formHasSubmitted: state.createCampaign.formHasSubmitted,
  isSuperuser: state.users.isSuperuser,
  userPermissions: state.users.userPermissions,
  campaignId: state.createCampaign.campaign?.pk,
});

export default connect(mapPropsToState)(CreateCampaignStepFourV2);
